import React, {useEffect, Suspense, lazy} from 'react';
import PropTypes from 'prop-types';
import {Route, Routes, useLocation} from 'react-router-dom';

import {useMarketingAttributionConsumer} from '../../hook/appUrl';
import Loading from '../loading/Loading';

import NotFound from '../../page/notFound/NotFound';
import Home from '../../page/home/Home';
import Product from '../../page/product/Product';

// 첫 진입/핵심 SSR 라우트만 eager import로 유지
const Brand = lazy(() => import('../../page/brand/Brand'));
const Search = lazy(() => import('../../page/search/Search'));
const Discover = lazy(() => import('../../page/discover/Discover'));
const Seller = lazy(() => import('../../page/seller/Seller'));
const BrandRanking = lazy(() => import('../../page/brandRanking/BrandRanking'));
const Download = lazy(() => import('../../page/download/Download'));
const Collection = lazy(() => import('../../page/collection/Collection'));
const Collections = lazy(() => import('../../page/collections/Collections'));
const Login = lazy(() => import('../../page/auth/login/Login.jsx'));
const SignUp = lazy(() => import('../../page/auth/signUp/SignUp'));
const FriendInvite = lazy(() => import('../../page/friendInvite/FriendInvite'));
const SellerCollection = lazy(() => import('../../page/sellerCollection/SellerCollection'));
const Sell = lazy(() => import('../../page/sell/Sell'));
const Fees = lazy(() => import('../../page/fees/Fees'));
const CollectionCortis = lazy(() => import('../../page/collectionCortis/CollectionCortis'));
const SignUpSns = lazy(() => import('../../page/auth/signUp/SignUpSns'));
const SocialAuthComplete = lazy(() => import('../../page/auth/socialAuthComplete/SocialAuthComplete'));
const MyPageWrapper = lazy(() => import('../../page/myPage/MyPageWrapper'));
const Settings = lazy(() => import('../../page/settings/Settings'));
const PurchasedWrapper = lazy(() => import('../../page/purchased/PurchasedWrapper'));
const OrderWrapper = lazy(() => import('../../page/order/OrderWrapper'));
const Upload = lazy(() => import('../../page/upload/Upload'));
const CheckoutWrapper = lazy(() => import('../../page/checkout/CheckoutWrapper'));
const RegisterCheckoutShipping = lazy(() => import('../../page/checkout/shipping/RegisterCheckoutShipping'));
const CheckoutComplete = lazy(() => import('../../page/checkout/complete/CheckoutComplete'));
const CheckoutFail = lazy(() => import('../../page/checkout/fail/CheckoutFail').then(m => ({ default: m.CheckoutFail })));
const ErrorPage = lazy(() => import('../../page/errorPage/ErrorPage'));
const BrandRadar = lazy(() => import('../../page/brandRadar/BrandRadar'));

/**
 * Component with contains all the global routes.
 * @param hostname the webapp hostname
 * @param location a location object to be used for matching children elements instead of the current history location
 * @param userAgent the user agent
 * @return {*}
 * @constructor
 */
export default function AppRoutes({ hostname, userAgent }) {
    const { pathname } = useLocation();

    useEffect(() => {
        // scroll to top on root page change (only when pathname changes)
        window.scrollTo(0, 0);
    }, [pathname]);

    // consume marketing attribution from location parameters
    useMarketingAttributionConsumer();

    // build the UI elements
    return (
        <Suspense fallback={<Loading />}>
            <Routes>
                <Route path="/" element={<Home hostname={hostname} />} />
                <Route path="/brand/:brandName/:categoryName?" element={<Brand hostname={hostname}/>} />
                <Route path="/collection/:collectionId/*" element={<Collection hostname={hostname} userAgent={userAgent} />} />
                <Route path="/collections" element={<Collections hostname={hostname} />} />
                <Route path="/discover/:discoverTab?/:categoryName?" element={<Discover hostname={hostname} userAgent={userAgent} />} />
                <Route path="/download" element={<Download hostname={hostname}/>} />
                <Route path="/sell" element={<Sell hostname={hostname} userAgent={userAgent}/>} />
                <Route path="/sell/fees" element={<Fees hostname={hostname} userAgent={userAgent}/>} />
                <Route path="/brand-ranking" element={<BrandRanking hostname={hostname} userAgent={userAgent} />} />
                <Route path="/brand-radar/:brandName?" element={<BrandRadar hostname={hostname} userAgent={userAgent} />} />
                <Route path="/product/:productId/*" element={<Product hostname={hostname} userAgent={userAgent} />} />
                <Route path="/upload" element={<Upload hostname={hostname} userAgent={userAgent} />} />
                <Route path="/seller/:sellerId/:encodedTitle?/:sellerTab?" element={<Seller hostname={hostname} userAgent={userAgent} />} />
                <Route path="/search" element={<Search hostname={hostname}/>} />
                <Route path="/search/:keyword/:categoryName?" element={<Search hostname={hostname}/>} />
                <Route path="/order/purchased" element={<Download hostname={hostname}/>} />
                <Route path="/order/selling" element={<Download hostname={hostname}/>} />
                <Route path="/login" element={<Login />} />
                <Route path="/signUp" element={<SignUp />} />
                <Route path="/signUpSns" element={<SignUpSns />} />
                <Route path="/auth/kakao/complete" element={<SocialAuthComplete socialType={'KAKAO'} />} />
                <Route path="/auth/apple/complete" element={<SocialAuthComplete socialType={'APPLE'}/>} />
                <Route path="/auth/facebook/complete" element={<SocialAuthComplete socialType={'FACEBOOK'}/>} />
                <Route path="/my/:sellerTab?" element={<MyPageWrapper hostname={hostname} userAgent={userAgent} />} />
                <Route path="/my/purchased" element={<PurchasedWrapper hostname={hostname} userAgent={userAgent} />} />
                <Route path="/my/purchased/:orderId" element={<OrderWrapper hostname={hostname} userAgent={userAgent} />} />
                <Route path="/my/settings" element={<Settings hostname={hostname} userAgent={userAgent} />} />

                <Route path="/checkout/:productId" element={<CheckoutWrapper hostname={hostname}/>}/>
                <Route path="/checkout/shipping/:productId" element={<RegisterCheckoutShipping />}/>
                <Route path="/checkout/complete" element={<CheckoutComplete />}/>
                <Route path="/checkout/fail" element={<CheckoutFail />}/>
                <Route path="/event/join/:code" element={<FriendInvite hostname={hostname} />}/>
                <Route path="/vintage-shops/:sellerCollectionId?/*" element={<SellerCollection hostname={hostname} />}/>
                <Route path="/feature/cortis" element={<CollectionCortis />} />
                <Route path="/temporary-wrong" element={<ErrorPage />}/>
                <Route path="*" element={<NotFound />} />
            </Routes>
        </Suspense>
    );
}
AppRoutes.propTypes = {
    hostname: PropTypes.string.isRequired,
    location: PropTypes.object,
    userAgent: PropTypes.string.isRequired
};
